import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject, ReplaySubject } from "rxjs";
import { WebCustomer, WebCustomerAgingVariables, CustomerClient, CustomerPasswordUpdate } from "../models";
import { AuthService } from "./auth.service";
import { SessionDataService } from "./session-data.service";
import { distinctUntilChanged, filter, switchMap, tap } from "rxjs/operators";
import { ErrorLoggingService } from "./error-logging.service";

@Injectable({
    providedIn: "root",
})
export class CustomerService 
{
    private customerSubject: BehaviorSubject<WebCustomer> = new BehaviorSubject<WebCustomer>(null);
    public customer: Observable<WebCustomer> = this.customerSubject.asObservable().pipe(filter((webCustomerInstance): webCustomerInstance is WebCustomer => !!webCustomerInstance));

    private customerIdSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    public customerId: Observable<string> = this.customerIdSubject.asObservable();

    private customerAgingSubject = new BehaviorSubject<WebCustomerAgingVariables>(null);
    public customerAging: Observable<WebCustomerAgingVariables> = this.customerAgingSubject.asObservable().pipe(filter((customerAgingInstance): customerAgingInstance is WebCustomerAgingVariables => !!customerAgingInstance));

    private isDataReadySubject = new ReplaySubject<boolean>(1);
    public isDataReady$ = this.isDataReadySubject.asObservable();

    constructor
        (
            private customerClient: CustomerClient,
            private authService: AuthService,
            private sessionDataService: SessionDataService,
            private errorLoggingService: ErrorLoggingService
        )
    {
        this.authService.currentUserInfo.pipe(
            filter(user => this.authService.isLoggedIn() && !user.isCompany && user.name !== '')
        ).subscribe(user =>
        {
            this.customerIdSubject.next(user.name);
        });
        this.customerIdSubject.pipe(
            distinctUntilChanged(),
            filter(id => !!id),
            tap(() =>
            {
                this.customerSubject.next(null);
                this.customerAgingSubject.next(null);
                this.isDataReadySubject.next(false);
            }),
            switchMap(id =>
                this.customerClient.getWebCustomerV2(id).pipe(
                    tap(customer =>
                    {
                        this.customerSubject.next(customer);
                        this.sessionDataService.CustId = customer?.CustId ?? '';
                    }),
                    switchMap(customer => this.customerClient.getWebCustomerAging(customer.BranchCustId))
                )
            )
        ).subscribe({
            next: aging =>
            {
                this.customerAgingSubject.next(aging);
                this.isDataReadySubject.next(true)
            },
            error: err =>
            {
                this.errorLoggingService.logError(err);
                this.customerSubject.next(null);
                this.customerAgingSubject.next(null);
                this.customerIdSubject.next(null);
                this.isDataReadySubject.next(false);
            }
        });
    }

    public pendingDeliveries(branchCustId: string, loc: number)
    {
        return this.customerClient.pendingDeliveries(branchCustId, loc);
    }
    public clearCustomerData(): void
    {
        this.customerSubject.next(null);
        this.customerIdSubject.next(null);
        this.customerAgingSubject.next(null);
    }

    public populateData()
    {
        this.getCustomerInfo();
    }

    public selectCustomer(customerItem: WebCustomer): void
    {
        this.customerIdSubject.next(customerItem.BranchCustId);
        this.populateData();
    }

    private getCustomerInfo(): void
    {
        this.customerClient.getWebCustomerV2(this.customerIdSubject.value).subscribe(e =>
        {
            this.customerSubject.next(e)
            this.sessionDataService.CustId = e?.CustId ?? "";
            this.getCustomerAging(e.BranchCustId)
        });
    }

    private getCustomerAging(custID): void
    {
        this.customerClient.getWebCustomerAging(custID)
            .subscribe(e => this.customerAgingSubject.next(e));
    }

    public doesLocationHaveAPendingSWO(branchCustId: string, loc: number)
    {
        return this.customerClient.doesLocationHavePendingSWO(branchCustId, loc);
    }

    public updatePassword(custId: string, accountNo: number, oldPassword: string, newPassword: string): Observable<boolean>
    {
        const customerPasswordUpdate: CustomerPasswordUpdate =
        {
            AccountNumber: accountNo,
            BranchCustId: custId,
            OldPassword: oldPassword,
            NewPassword: newPassword
        }
        return this.customerClient.updatePassword(customerPasswordUpdate);
    }

}
